import { defineStore } from 'pinia';
import { ExperienceRepository, type CreateExperienceData } from "~/api/v2/experiences/ExperienceRepository";
import { useExperienceStatisticsStore } from './experienceStatistics';
import { authStore } from './auth';

export interface ExperienceDraft {
    currentStep: number;
    step1Data: Record<string, any>;
    step2Data: Record<string, any>;
    step3Data: Record<string, any>;
    step4Data: Record<string, any>;
    lastUpdated: number;
}

export const useExperienceStore = defineStore('experiences', {
    state: () => ({
        experiences: [] as any[],
        experience: null as any | null,
        draft: null as ExperienceDraft | null,
        loading: false,
    }),
    actions: {
        async fetchExperiences(params: Record<string, any> = {}) {
            this.loading = true;
            return useNuxtApp().$api().v2.experiences.list(params).then((response: any) => {
                // New response shape: { object, data, total, offset, limit }.
                // Fallback to the bare array for safety if an older response slips through.
                const data = Array.isArray(response) ? response : (response?.data ?? []);
                this.experiences = data;
                return data;
            }).finally(() => {
                this.loading = false;
            });
        },
        async fetchExperience(id: string) {
            return useNuxtApp().$api().v2.experiences.get(id).then(response => {
                this.experience = response;

                return response
            });
        },
        async createExperience(data: CreateExperienceData) {
            return await useNuxtApp().$api().v2.experiences.create(data).then(response => {
                this.experiences.unshift(response);
                const statisticsStore = useExperienceStatisticsStore();
                const auth = authStore();
                if (auth.user) {
                    statisticsStore.fetchStatistics(auth.user.id);
                }

                return response;
            });
        },
        async deleteExperience(id: string) {
            await useNuxtApp().$api().v2.experiences.remove(id).then(response => {
                this.experiences = this.experiences.filter(exp => exp.id !== id);
                const statisticsStore = useExperienceStatisticsStore();
                const auth = authStore();
                if (auth.user) {
                    statisticsStore.fetchStatistics(auth.user.id);
                }
                return response;
            });
        },
        async updateExperience(id: string, data: CreateExperienceData) {
            await useNuxtApp().$api().v2.experiences.update(id, data).then(response => {
                const index = this.experiences.findIndex(exp => exp.id === id);
                if (index !== -1) {
                    this.experiences[index] = response.data;
                }
                const statisticsStore = useExperienceStatisticsStore();
                const auth = authStore();
                if (auth.user) {
                    statisticsStore.fetchStatistics(auth.user.id);
                }
                return response;
            });
        },
        async uploadExperienceLogo(experienceId: string, file: File): Promise<string | null> {
            const response = await useNuxtApp().$api().v2.experiences.uploadLogo(experienceId, file);
            return response.signed_logo_url;
        },
        async submitExperienceLogoRequest(experienceId: string, file: File): Promise<string> {
            const response = await useNuxtApp().$api().v2.experiences.submitLogoRequest(experienceId, file);
            return response.logo_change_request_id;
        },

        // Draft management actions
        saveDraft(draftData: Omit<ExperienceDraft, 'lastUpdated'>) {
            this.draft = {
                ...draftData,
                lastUpdated: Date.now(),
            };
        },

        clearDraft() {
            this.draft = null;
        },

        hasDraft(): boolean {
            return this.draft !== null;
        },

        getDraft(): ExperienceDraft | null {
            return this.draft;
        },
    }
})